import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Container, Typography, Paper } from '@mui/material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo);
  }

  private handleReturnToLogin = () => {
    // Clear cookies across host and domain variants
    document.cookie = 'has_session=; Path=/; Max-Age=0;';
    document.cookie = `has_session=; Path=/; Domain=${window.location.hostname}; Max-Age=0;`;
    const parts = window.location.hostname.split('.');
    if (parts.length > 2) {
      const parentDomain = parts.slice(1).join('.');
      document.cookie = `has_session=; Path=/; Domain=.${parentDomain}; Max-Age=0;`;
    }
    window.location.href = '/login';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            bgcolor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={2}
              sx={{
                p: { xs: 3, sm: 5 },
                textAlign: 'center',
                border: '1px solid',
                borderColor: '#e0e0e0',
                borderRadius: 2,
                bgcolor: '#ffffff',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <ErrorIcon sx={{ fontSize: 56, color: 'error.main' }} />
              </Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Something went wrong
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your session may have expired or a temporary error occurred.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={this.handleReturnToLogin}
                  sx={{ textTransform: 'none' }}
                >
                  Return to Sign In
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.location.reload()}
                  sx={{ textTransform: 'none' }}
                >
                  Reload Page
                </Button>
              </Box>
            </Paper>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
